import React, { useState, useEffect } from 'react';
import { Link, Outlet, useLocation } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { admin } from '@/lib/api';
import { 
  LayoutDashboard, 
  Package, 
  ShoppingCart, 
  Users, 
  BarChart3,
  Menu,
  X,
  Warehouse,
  DollarSign,
  FolderOpen,
  Settings,
  Image,
  Activity,
  Clock,
  CreditCard
} from 'lucide-react';
import { Button } from '@/components/ui/button';

const AdminLayout = ({ children }) => {
  const { user } = useAuth();
  const location = useLocation();
  const [stats, setStats] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    loadStats();
  }, []);

  const loadStats = async () => {
    try {
      const response = await admin.getStats();
      setStats(response.data);
    } catch (error) {
      console.error('Failed to load stats:', error);
    }
  };

  const navItems = [
    { name: 'Dashboard', href: '/admin', icon: LayoutDashboard },
    { name: 'Analytics', href: '/admin/analytics', icon: BarChart3 },
    { name: 'Customer Activity', href: '/admin/activity', icon: Activity },
    { name: 'Products', href: '/admin/products', icon: Package },
    { name: 'Backorder', href: '/admin/out-of-stock', icon: Clock },
    { name: 'Categories', href: '/admin/categories', icon: FolderOpen },
    { name: 'Banners', href: '/admin/banners', icon: Image },
    { name: 'Inventory', href: '/admin/inventory', icon: Warehouse },
    { name: 'Pricing', href: '/admin/pricing', icon: DollarSign },
    { name: 'Orders', href: '/admin/orders', icon: ShoppingCart },
    { name: 'Customers', href: '/admin/users', icon: Users },
    { name: 'Credit Applications', href: '/admin/credit-applications', icon: CreditCard },
    { name: 'Settings', href: '/admin/settings', icon: Settings },
  ];

  const isActive = (href) => {
    if (href === '/admin') return location.pathname === '/admin';
    return location.pathname.startsWith(href);
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC]">
      {/* Mobile Header */}
      <div className="lg:hidden bg-white border-b border-[#E2E8F0] p-4 flex justify-between items-center">
        <span className="text-lg font-bold text-[#0F172A]">Admin Panel</span>
        <Button variant="ghost" onClick={() => setSidebarOpen(!sidebarOpen)}>
          {sidebarOpen ? <X className="w-5 h-5" /> : <Menu className="w-5 h-5" />}
        </Button>
      </div>

      <div className="flex">
        {/* Sidebar */}
        <aside className={`
          fixed lg:static inset-y-0 left-0 z-50
          w-64 bg-[#0F172A] text-white
          transform transition-transform duration-200 ease-in-out
          ${sidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        `}>
          <div className="p-6 border-b border-[#1E293B]">
            <Link to="/" className="block">
              <img 
                src="https://irishconnexxion.com.au/wp-content/uploads/2021/06/Asset-1@3x.png" 
                alt="Irish Connexxion" 
                className="h-8 w-auto brightness-0 invert"
              />
            </Link>
            <p className="text-xs text-[#64748B] mt-2">Admin Panel</p>
          </div>

          <nav className="p-4 space-y-1">
            {navItems.map((item) => (
              <Link
                key={item.name}
                to={item.href}
                onClick={() => setSidebarOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 text-sm transition-colors ${
                  isActive(item.href)
                    ? 'bg-[#002FA7] text-white'
                    : 'text-[#94A3B8] hover:bg-[#1E293B] hover:text-white'
                }`}
                data-testid={`admin-nav-${item.name.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                {item.name}
              </Link>
            ))}
          </nav>

          {/* Stats Summary */}
          {stats && (
            <div className="p-4 mt-4 border-t border-[#1E293B]">
              <div className="grid grid-cols-2 gap-4">
                <div className="bg-[#1E293B] p-3">
                  <p className="text-xs text-[#64748B]">Orders</p>
                  <p className="text-lg font-bold">{stats.total_orders}</p>
                </div>
                <div className="bg-[#1E293B] p-3">
                  <p className="text-xs text-[#64748B]">Pending</p>
                  <p className="text-lg font-bold text-yellow-400">{stats.pending_orders}</p>
                </div>
              </div>
            </div>
          )}
        </aside>

        {/* Main Content */}
        <main className="flex-1 min-h-screen">
          <div className="p-6 lg:p-8">
            {children}
          </div>
        </main>
      </div>

      {/* Overlay for mobile */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
